import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { GuideProvider } from "@/context/GuideContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Spinner from "@/components/ui/Spinner";
import NewVersionBanner from "@/components/ui/NewVersionBanner";
import GEODashboard from "@/pages/GEODashboard";
import GEOComparison from "@/pages/GEOComparison";
import OptimizeContent from "@/pages/OptimizeContent";
import QueryResearch from "@/pages/QueryResearch";
import AnswerEngine from "@/pages/AnswerEngine";
import LoginPage from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import VerifyOTP from "@/pages/VerifyOTP";
import SetNewPassword from "@/pages/SetNewPassword";
import SuperAdmin from "@/pages/SuperAdmin";
import AdminMembers from "@/pages/Admin";
import NotFound from "@/pages/NotFound";
import Settings from "@/pages/Settings";
import UserProfile from "@/pages/UserProfile";
import TrackingPage from "@/pages/TrackingPage";
import EditQueryPage from "@/pages/EditQueryPage";
import HistoricalQueryGD from "./pages/HistoricalQueryGD";
import AIAssistantSidebar from "@/layout/AIAssistantSidebar";
import TourRunner from "@/components/tour/TourRunner";
import Footer from "@/layout/Footer";
import NotificationToastStack from "@/components/notifications/NotificationToastStack";
import ErrorBoundary from "@/components/ui/ErrorBoundary";
import * as notificationService from "@/services/notificationService";

function FullPageSpinner() {
  return (
    <div className="d-flex justify-content-center align-items-center vh-100" style={{ backgroundColor: "#f9fafb" }}>
      <Spinner />
    </div>
  );
}

function GuestRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <FullPageSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/geo-dashboard" replace />;
  }

  return children;
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      notificationService.startPolling();
    } else {
      notificationService.stopPolling();
    }
    return () => {
      notificationService.stopPolling();
    };
  }, [isAuthenticated]);

  if (isLoading) {
    return <FullPageSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <ErrorBoundary>
        {children || <Outlet />}
      </ErrorBoundary>
      <AIAssistantSidebar />
      <TourRunner />
      <NewVersionBanner />
      <NotificationToastStack />
      <Footer />
    </>
  );
}

function App() {
  return (
    <GuideProvider>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Router>
        <AuthProvider>
          <Routes>
            <Route element={<ProtectedRoute />}>
              <Route
                path="/"
                element={<Navigate to="/geo-dashboard" replace />}
              />
              <Route path="/geo-dashboard" element={<GEODashboard />} />
              <Route path="/geo-comparison" element={<GEOComparison />} />
              <Route path="/optimize-content" element={<OptimizeContent />} />
              <Route path="/prompt-research" element={<QueryResearch />} />
              <Route path="/answer-engine" element={<AnswerEngine />} />
              <Route path="/super-admin" element={<SuperAdmin />} />
              <Route path="/member" element={<AdminMembers />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/profile" element={<UserProfile />} />
              <Route path="/tracking" element={<TrackingPage />} />
              <Route path="/edit-prompt" element={<EditQueryPage />} />
              <Route path="/archived-prompt-gd" element={<HistoricalQueryGD />} />
            </Route>

            <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
            <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
            <Route path="/verify-otp" element={<GuestRoute><VerifyOTP /></GuestRoute>} />
            <Route path="/set-new-password" element={<GuestRoute><SetNewPassword /></GuestRoute>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </Router>
    </GuideProvider>
  );
}

export default App;
